import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const corporateNotes = [
  {
    id: "abaxx",
    name: "Abaxx",
    client: "Abaxx Technologies",
    weight: "1 Gram Fine Gold 9999",
    images: {
      obverse: "/abaxx-obverse.jpg",
      reverse: "/abaxx-reverse.jpg",
    },
    description: "A groundbreaking collaboration featuring Abaxx's 'Building Smarter Markets' vision with distinctive red and gold design.",
  },
  {
    id: "amherst",
    name: "Amherst",
    client: "Amherst Crane Rentals & Concrete Pumping",
    weight: "1 Gram Fine Gold 9999",
    images: {
      obverse: "/amherst-obverse.png",
      reverse: "/amherst-reverse.png",
    },
    description: "Celebrating Canadian industry heritage with stunning maple leaf imagery and crane operations photography.",
  },
];

// Corporate Note Card Component
const CorporateNoteCard = ({ note }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <div className="group">
      <div 
        className="relative bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-lg overflow-hidden cursor-pointer"
        onMouseEnter={() => setIsFlipped(true)}
        onMouseLeave={() => setIsFlipped(false)}
      >
        {/* Badge */}
        <div className="absolute top-4 left-4 z-20 px-3 py-1.5 bg-[#D4AF37] text-black text-[0.55rem] tracking-[0.15em] uppercase font-medium rounded">
          Corporate
        </div>
        
        {/* Flip Container */}
        <div className="relative aspect-[4/3] p-6" style={{ perspective: '1000px' }}>
          <div 
            className="w-full h-full transition-transform duration-700"
            style={{ 
              transformStyle: 'preserve-3d',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
            }}
          >
            {/* Front */}
            <div 
              className="absolute inset-0 flex items-center justify-center p-6"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <img 
                src={note.images.obverse} 
                alt={`${note.name} - Obverse`}
                className="max-w-full max-h-full object-contain"
              />
            </div>
            {/* Back */}
            <div 
              className="absolute inset-0 flex items-center justify-center p-6"
              style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
            >
              <img 
                src={note.images.reverse} 
                alt={`${note.name} - Reverse`}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          </div>
        </div>
        
        {/* Hover indicator */}
        <div className="absolute bottom-4 right-4 px-2 py-1 bg-black/70 text-[0.5rem] tracking-wider text-[#D4AF37]/70 uppercase rounded opacity-60 group-hover:opacity-0 transition-opacity">
          Hover to flip
        </div>
      </div>
      
      {/* Info */}
      <div className="mt-6 text-center">
        <p className="text-[0.6rem] tracking-[0.2em] uppercase text-[#D4AF37]/70 mb-2">{note.client}</p>
        <h3 className="text-2xl text-white/90 mb-3" style={{ fontFamily: 'Cinzel, serif' }}>{note.name}</h3>
        <p className="text-sm text-white/70 font-light leading-relaxed">{note.description}</p>
      </div>
    </div>
  );
};

export const Corporate = () => {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    quantity: "",
    message: "",
  });
  const [brandColors, setBrandColors] = useState(["#D4AF37"]);
  const [materials, setMaterials] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addColor = () => {
    if (brandColors.length < 5) {
      setBrandColors([...brandColors, "#000000"]);
    }
  };

  const updateColor = (index, value) => {
    const newColors = [...brandColors];
    newColors[index] = value;
    setBrandColors(newColors);
  };

  const removeColor = (index) => {
    if (brandColors.length > 1) {
      setBrandColors(brandColors.filter((_, i) => i !== index));
    }
  };

  const handleMaterialUpload = (e) => {
    const files = Array.from(e.target.files);
    const newMaterials = files.map(file => ({
      file,
      name: file.name,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    setMaterials([...materials, ...newMaterials]);
  };

  const removeMaterial = (index) => {
    const newMaterials = [...materials];
    if (newMaterials[index].preview) {
      URL.revokeObjectURL(newMaterials[index].preview);
    }
    newMaterials.splice(index, 1);
    setMaterials(newMaterials);
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return '🖼';
    if (type.includes('pdf')) return '📄';
    if (type.includes('document') || type.includes('word')) return '📝';
    if (type.includes('spreadsheet') || type.includes('excel')) return '📊';
    return '📎';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // First upload any materials
      let uploadedFileIds = [];
      if (materials.length > 0) {
        const formDataFiles = new FormData();
        materials.forEach(material => {
          formDataFiles.append('files', material.file);
        });
        
        try {
          const uploadResponse = await axios.post(`${API}/upload-multiple`, formDataFiles, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedFileIds = uploadResponse.data.files
            .filter(f => f.success)
            .map(f => f.file_id);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          // Continue with submission even if upload fails
        }
      }

      // Submit the inquiry with file references
      const submitData = {
        company_name: formData.company_name,
        contact_name: formData.contact_name,
        email: formData.email,
        phone: formData.phone || null,
        quantity: formData.quantity || null,
        brand_colors: brandColors,
        materials_count: materials.length,
        uploaded_file_ids: uploadedFileIds.length > 0 ? uploadedFileIds : null,
        message: `
Quantity: ${formData.quantity || 'Not specified'}
Brand Colors: ${brandColors.join(', ')}

Additional Details:
${formData.message || 'No additional details provided'}
        `.trim()
      };
      
      await axios.post(`${API}/inquiries`, submitData);
      toast.success("Thank you for your inquiry. We will contact you shortly.");
      setFormData({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        quantity: "",
        message: "",
      });
      setBrandColors(["#D4AF37"]);
      setMaterials([]);
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="corporate-page">
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-32 md:pb-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[0.625rem] tracking-[0.4em] uppercase text-[#D4AF37]/80 mb-8 animate-fade-in">
              Business Solutions
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-8 animate-fade-in-up">
              <span className="gold-gradient">Corporate</span>
              <span className="text-white/90"> & Custom Notes</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 font-light leading-relaxed max-w-xl mx-auto animate-fade-in-up-delay-1">
              Partner with Goldnote to create bespoke Goldnotes for your organization, 
              special events, or corporate gifting programs.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content with Sticky Form */}
      <div className="container mx-auto px-6 lg:px-12 pb-32">
        <div className="flex flex-col lg:flex-row gap-16 xl:gap-24">
          {/* Left Column - Scrollable Content */}
          <div className="flex-1">
            {/* Corporate Notes Showcase */}
            <section className="mb-24" data-testid="corporate-notes-section">
              <div className="mb-12">
                <p className="text-[0.625rem] tracking-[0.4em] uppercase text-[#D4AF37]/80 mb-4">
                  Featured Work
                </p>
                <h2 className="text-3xl md:text-4xl text-white/90" style={{ fontFamily: 'Cinzel, serif' }}>
                  Corporate Specialties
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {corporateNotes.map((note) => (
                  <CorporateNoteCard key={note.id} note={note} />
                ))}
              </div>
            </section>

            {/* Services Section */}
            <section data-testid="services-section">
              <div className="mb-12">
                <p className="text-[0.625rem] tracking-[0.4em] uppercase text-[#D4AF37]/80 mb-4">
                  What We Offer
                </p>
                <h2 className="text-3xl md:text-4xl text-white/90" style={{ fontFamily: 'Cinzel, serif' }}>
                  Our Services
                </h2>
              </div>
              
              <div className="space-y-6">
                <div className="p-8 bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500 rounded-lg">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 shrink-0 border border-[#D4AF37]/30 flex items-center justify-center rounded-lg">
                      <svg className="w-6 h-6 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-white/90 mb-3">Corporate Branding</h3>
                      <p className="text-sm text-white/70 font-light leading-relaxed">
                        Custom Goldnotes featuring your company logo and branding for executive gifts, 
                        milestones, and special recognition.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500 rounded-lg">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 shrink-0 border border-[#D4AF37]/30 flex items-center justify-center rounded-lg">
                      <svg className="w-6 h-6 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-white/90 mb-3">Event Commemoratives</h3>
                      <p className="text-sm text-white/70 font-light leading-relaxed">
                        Limited edition Goldnotes for conferences, anniversaries, product launches, 
                        and other significant corporate events.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500 rounded-lg">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 shrink-0 border border-[#D4AF37]/30 flex items-center justify-center rounded-lg">
                      <svg className="w-6 h-6 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-white/90 mb-3">Bulk Orders</h3>
                      <p className="text-sm text-white/70 font-light leading-relaxed">
                        Volume pricing for organizations looking to incorporate Goldnotes 
                        into their gifting programs or incentive structures.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Right Column - Sticky Form */}
          <div className="lg:w-[420px] lg:shrink-0">
            <div className="lg:sticky lg:top-28" data-testid="inquiry-section">
              <div className="mb-6">
                <p className="text-[0.625rem] tracking-[0.4em] uppercase text-[#D4AF37]/80 mb-3">
                  Get Started
                </p>
                <h2 className="font-serif text-2xl text-white/90 mb-2">
                  Request Custom Notes
                </h2>
                <p className="text-xs text-white/40 font-light leading-relaxed">
                  Fill out the form and we'll get back to you within 1-2 business days.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-xl" data-testid="inquiry-form">
                {/* Row 1: Company & Contact */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="company_name" className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                      Company *
                    </Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      className="bg-[#121212] border-white/10 text-white placeholder:text-white/20 focus:border-[#D4AF37]/50 h-10 text-sm"
                      placeholder="Company"
                      data-testid="input-company-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact_name" className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                      Contact *
                    </Label>
                    <Input
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      required
                      className="bg-[#121212] border-white/10 text-white placeholder:text-white/20 focus:border-[#D4AF37]/50 h-10 text-sm"
                      placeholder="Your name"
                      data-testid="input-contact-name"
                    />
                  </div>
                </div>

                {/* Row 2: Email & Phone */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-[#121212] border-white/10 text-white placeholder:text-white/20 focus:border-[#D4AF37]/50 h-10 text-sm"
                      placeholder="email@co.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-[#121212] border-white/10 text-white placeholder:text-white/20 focus:border-[#D4AF37]/50 h-10 text-sm"
                      placeholder="+1 (555) 000-0000"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                {/* Quantity */}
                <div className="space-y-1.5">
                  <Label htmlFor="quantity" className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                    Estimated Quantity
                  </Label>
                  <Input
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    className="bg-[#121212] border-white/10 text-white placeholder:text-white/20 focus:border-[#D4AF37]/50 h-10 text-sm"
                    placeholder="e.g., 50-100 units"
                    data-testid="input-quantity"
                  />
                </div>

                {/* Brand Colors */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                      Brand Colors
                    </Label>
                    {brandColors.length < 5 && (
                      <button
                        type="button"
                        onClick={addColor}
                        className="text-[0.6rem] tracking-wider uppercase text-[#D4AF37] hover:text-[#EEDC9A] transition-colors flex items-center gap-1"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Color
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {brandColors.map((color, index) => (
                      <div key={index} className="flex items-center gap-1.5 bg-[#121212] rounded-lg px-2 py-1.5 border border-white/10">
                        <input
                          type="color"
                          value={color}
                          onChange={(e) => updateColor(index, e.target.value)}
                          className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded [&::-webkit-color-swatch]:border [&::-webkit-color-swatch]:border-white/20"
                        />
                        <span className="text-xs text-white/80 font-mono w-16">{color}</span>
                        {brandColors.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeColor(index)}
                            className="w-5 h-5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Materials Upload */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                      Materials & Assets
                    </Label>
                    <span className="text-[0.55rem] text-white/30">Logos, images, PDFs, etc.</span>
                  </div>
                  
                  <label 
                    htmlFor="materials-upload"
                    className="flex items-center justify-center gap-3 h-16 border-2 border-dashed border-white/10 hover:border-[#D4AF37]/40 rounded-lg cursor-pointer transition-colors bg-[#121212]/50"
                  >
                    <svg className="w-5 h-5 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-xs text-white/40">Drop files or click to upload</span>
                    <input
                      id="materials-upload"
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ai,.eps,.svg"
                      onChange={handleMaterialUpload}
                      className="hidden"
                      data-testid="input-materials"
                    />
                  </label>

                  {/* Uploaded Materials */}
                  {materials.length > 0 && (
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                      {materials.map((material, index) => (
                        <div key={index} className="flex items-center gap-2 bg-[#121212] rounded px-3 py-2 border border-white/5">
                          {material.preview ? (
                            <img src={material.preview} alt="" className="w-8 h-8 object-cover rounded" />
                          ) : (
                            <span className="text-lg">{getFileIcon(material.type)}</span>
                          )}
                          <span className="flex-1 text-xs text-white/80 truncate">{material.name}</span>
                          <button
                            type="button"
                            onClick={() => removeMaterial(index)}
                            className="w-5 h-5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Project Details */}
                <div className="space-y-1.5">
                  <Label htmlFor="message" className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                    Project Details *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="bg-[#121212] border-white/10 text-white placeholder:text-white/20 focus:border-[#D4AF37]/50 resize-none text-sm"
                    placeholder="Tell us about your vision - occasion, design elements, branding..."
                    data-testid="input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#D4AF37] text-[#050505] hover:bg-[#EEDC9A] font-sans text-xs tracking-[0.15em] uppercase py-5 rounded-lg"
                  data-testid="submit-inquiry-btn"
                >
                  {isSubmitting ? "Submitting..." : "Request Quote"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Corporate;
